/**
 * Everything the server may refuse, by code. The server sends the code alongside the
 * English text, so each player's screen can say it in their own language. scope.md §10.
 *
 * An engine refusal ("Not your turn") has no code of its own: it arrives as `notAllowed`
 * with the engine's English wording as the detail.
 */

export const SERVER_ERRORS = {
  messageTooLarge: 'Message too large',
  malformed: 'Malformed JSON',
  invalidMessage: 'Invalid message',
  unknownMessage: 'Unknown message',
  alreadyInGame: 'You are already in a game',
  noSuchGame: 'No game with that code',
  joinFirst: 'Join a game first',
  gameFull: 'This game is full',
  alreadyStarted: 'This game has already started',
  seatGone: 'That seat is no longer available',
  hostOnlyRules: 'Only the host can change the rules',
  rulesLocked: 'The rules are locked once dealing begins',
  hostOnlyClaimTime: 'Only the host can change the time to ask',
  hostOnlyDeal: 'Only the host can deal',
  hostOnlyPlayAgain: 'Only the host can start another game',
  hostOnlyKick: 'Only the host can remove a player',
  nothingToReadyFor: 'There is nothing to get ready for yet',
  notInGame: 'You are not in this game',
  twoPlayersNeeded: 'Two players are needed',
  roundInProgress: 'A round is already in progress',
  matchOver: 'The match is over',
  matchNotOver: 'The game is not over yet',
  noGame: 'No game in progress',
  noClock: 'There is no clock running on you',
  kickTooSoon: 'A player can only be removed after two minutes away',
  couldNotRemove: 'Could not remove that player',
  tooManyReactions: 'Slow down a little',
  notAllowed: 'Not allowed',
  roomClosedEmpty: 'Room closed after being empty',
  timedOut: 'You ran out of time, so you were taken out of the game',
} as const;

export type ServerErrorCode = keyof typeof SERVER_ERRORS;

/** The code for a message the server sent, or `notAllowed` for an engine refusal. */
export function serverErrorCode(message: string): ServerErrorCode {
  for (const [code, text] of Object.entries(SERVER_ERRORS)) {
    if (text === message) return code as ServerErrorCode;
  }
  return 'notAllowed';
}
